import { useSearchParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

/**
 * Password Reset Page
 *
 * A verified App Link (Android) or Universal Link (iOS) delivers the recovery
 * tokens straight to the app, so this page is only reached when that did not
 * happen: an app older than 1.1.1, link handling switched off for the app, or
 * an in-app browser that will not hand the URL over.
 *
 * It used to forward those tokens onward as familywaffleapp://auth/reset-password.
 * Any installed app can register that scheme, and resolution between competing
 * claimants is undefined on both platforms, so a hostile app could receive a
 * live recovery token. It no longer does.
 *
 * There is no in-browser reset form to fall back to — resetting a password
 * re-wraps the account encryption key, which only the app can do — so the
 * honest thing to show is why the link did not open and how to fix it.
 */

const APP_STORE_URL = 'https://apps.apple.com/app/family-waffle/id6738030746';

function extractTokenFromHash(hash: string): { token?: string; code?: string; accessToken?: string; refreshToken?: string } {
  // Supabase sometimes puts tokens in the hash fragment
  const params = new URLSearchParams(hash.replace('#', ''));
  return {
    token: params.get('access_token') || undefined,
    code: params.get('code') || undefined,
    accessToken: params.get('access_token') || undefined,
    refreshToken: params.get('refresh_token') || undefined,
  };
}

// Heroicons key icon
const KeyIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-8 h-8">
    <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 5.25a3 3 0 0 1 3 3m3 0a6 6 0 0 1-7.029 5.912c-.563-.097-1.159.026-1.563.43L10.5 17.25H8.25v2.25H6v2.25H2.25v-2.818c0-.597.237-1.17.659-1.591l6.499-6.499c.404-.404.527-1 .43-1.563A6 6 0 1 1 21.75 8.25Z" />
  </svg>
);

export default function AuthResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const { t } = useTranslation('auth');

  // Extract token/code from query params or hash fragment
  const token = searchParams.get('token');
  const code = searchParams.get('code');
  const hashParams = extractTokenFromHash(location.hash);

  const authCode = code || token || hashParams.code || hashParams.token;

  const hasValidToken = authCode || hashParams.accessToken;

  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-4 bg-gradient-to-br from-gray-950 via-gray-900 to-gray-950">
      {/* Animated background gradient orbs */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 -left-4 w-96 h-96 bg-waffle-orange/20 rounded-full mix-blend-multiply filter blur-3xl opacity-70 animate-blob"></div>
        <div className="absolute top-0 -right-4 w-96 h-96 bg-amber-500/20 rounded-full mix-blend-multiply filter blur-3xl opacity-70 animate-blob animation-delay-2000"></div>
      </div>

      <div className="relative text-center max-w-md w-full">
        <div className="rounded-3xl p-6 sm:p-8 bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-xl border border-white/20 shadow-2xl">
          {/* Logo */}
          <img
            src="/logo.svg"
            alt="Family Waffle"
            className="w-20 h-20 mx-auto mb-6"
          />

          {/* Status Icon */}
          <div className="w-16 h-16 rounded-full bg-gradient-to-r from-waffle-orange to-amber-500 flex items-center justify-center text-white mx-auto mb-4">
            <KeyIcon />
          </div>

          {/* Title */}
          <h1 className="text-xl sm:text-2xl font-bold text-white mb-2">
            {hasValidToken ? t('resetPassword.resetYour') : t('resetPassword.passwordReset')}
          </h1>

          {/* Description */}
          <p className="text-sm sm:text-base text-gray-300 mb-6">
            {hasValidToken
              ? t('resetPassword.needsUpdate')
              : t('resetPassword.checkEmail')}
          </p>

          {/* App Store Link */}
          <a
            href={APP_STORE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block text-gray-400 hover:text-waffle-orange transition-colors text-sm"
          >
            {t('resetPassword.noApp')}
          </a>

        </div>
      </div>
    </div>
  );
}
